import { Box, Typography } from "@mui/material";
import React from "react";

import { monospaceStyle } from "../../theme";
import SourceTimeline from "../Detail/SourceTimeline";

// The accessible name of the block, for a test or a screen reader
export const SOURCE_COUNT = "Source count";

// A date or a number in a line of text: digits of equal width
export const Mono: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={monospaceStyle}>
    {children}
  </Box>
);

// "first 2026-03-12 · latest 2026-09-24", the dates in monospace
export const FirstAndLatest: React.FC<{ first: string; latest: string }> = ({
  first,
  latest,
}) => (
  <>
    first <Mono>{first}</Mono> · latest <Mono>{latest}</Mono>
  </>
);

export interface SourceCountBlockProps {
  // The number of documents behind the entity
  count: number;
  // The publication date of every source the response holds, for the
  // timeline; an undated source has no dot
  dates: (string | null | undefined)[];
  // What follows "sources" on the line of the count
  after?: React.ReactNode;
  // The line beneath the count and the timeline; left out when empty
  footnote?: React.ReactNode;
  // At the right end of the line beneath
  action?: React.ReactNode;
}

/**
 * How much evidence there is and from when, in the head of a detail view:
 * a raised block of two rows. The first holds the number of sources, large
 * and in monospace, with what follows it, and the timeline of the sources
 * beside it; the second a caption, by default when the first and the latest
 * source appeared.
 */
const SourceCountBlock: React.FC<SourceCountBlockProps> = ({
  count,
  dates,
  after,
  footnote,
  action,
}) => (
  <Box
    role="group"
    aria-label={SOURCE_COUNT}
    sx={{
      display: "grid",
      gridTemplateColumns: "auto minmax(0, 1fr)",
      alignItems: "center",
      columnGap: "18px",
      rowGap: "6px",
      px: "14px",
      py: 1.5,
      backgroundColor: "background.raised",
      border: 1,
      borderColor: "divider",
      borderRadius: (theme) => `${theme.radius.surface}px`,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        flexWrap: "wrap",
        columnGap: 1,
      }}
    >
      <Box
        component="span"
        sx={{
          ...monospaceStyle,
          fontSize: "1.125rem",
          fontWeight: 500,
          lineHeight: 1.2,
        }}
      >
        {count}
      </Box>{" "}
      <Typography variant="caption" color="text.secondary">
        {count === 1 ? "source" : "sources"}
        {after}
      </Typography>
    </Box>
    <SourceTimeline dates={dates} />
    {(footnote || action) && (
      <Box
        sx={{
          gridColumn: "1 / -1",
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          flexWrap: "wrap",
          columnGap: 2,
        }}
      >
        <Typography variant="caption" color="text.disabled">
          {footnote}
        </Typography>
        {action}
      </Box>
    )}
  </Box>
);

export default SourceCountBlock;
